import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { parseInsightText, type InlineSpan } from './insightText';

/* ----------------------------------------------------------------------------
 * InsightText — the single safe renderer for model- and stored-insight text.
 *
 * Advisory / model bodies (SEO insights, the executive summary) can carry light
 * Markdown. Rendering them raw (whitespace-pre-wrap or a bare `{text}`) leaked
 * literal `#`, `**` and list markers on screen (owner screenshot). This wraps
 * the pure `parseInsightText` parser (see ./insightText.ts — unit-tested) and
 * lays the result out as short, mobile-readable paragraphs and lists: supported
 * inline emphasis renders as real elements, every raw marker is gone, and all
 * figures (currency, %, numbers) pass through verbatim. Pure presentational
 * leaf — no network, no state. Surfaces import THIS, never a per-page regex.
 * -------------------------------------------------------------------------- */

function renderSpans(spans: InlineSpan[]): ReactNode {
  return spans.map((s, i) => {
    if (s.kind === 'strong') return <strong key={i} className="font-semibold">{s.value}</strong>;
    if (s.kind === 'em') return <em key={i}>{s.value}</em>;
    if (s.kind === 'code')
      return (
        <code key={i} className="rounded bg-inset px-1 py-0.5 font-mono text-[0.9em]">
          {s.value}
        </code>
      );
    return <span key={i}>{s.value}</span>;
  });
}

/**
 * Render model / stored-insight text with no visible Markdown. Pass the raw
 * body; `className` styles the outer wrapper (colour / size flow to children).
 * Empty or whitespace-only text renders nothing.
 */
export function InsightText({
  text,
  className,
}: {
  text: string | null | undefined;
  className?: string;
}) {
  const blocks = parseInsightText(text ?? '');
  if (blocks.length === 0) return null;
  return (
    <div className={cn('space-y-2', className)}>
      {blocks.map((block, i) => {
        if (block.kind === 'ul') {
          return (
            <ul key={i} className="list-disc space-y-1 pl-5">
              {block.items.map((item, j) => (
                <li key={j}>{renderSpans(item)}</li>
              ))}
            </ul>
          );
        }
        if (block.kind === 'ol') {
          return (
            <ol key={i} className="list-decimal space-y-1 pl-5">
              {block.items.map((item, j) => (
                <li key={j}>{renderSpans(item)}</li>
              ))}
            </ol>
          );
        }
        return <p key={i}>{renderSpans(block.spans)}</p>;
      })}
    </div>
  );
}
